import { useSeoMeta } from '@unhead/react';
import { VideoFeed } from '@/components/VideoFeed';
import { Button } from '@/components/ui/button';
import { PlusCircle, FolderPlus } from 'lucide-react';
import { useState } from 'react';
import { AddBookmarkDialog } from '@/components/AddBookmarkDialog';
import { CollectionForm } from '@/components/CollectionForm';
import { LoginArea } from '@/components/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCollections } from '@/hooks/useCollections';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight } from 'lucide-react';

const Index = () => {
  useSeoMeta({
    title: 'NostrVidster - Video Bookmarking on Nostr',
    description: 'Discover, bookmark, and share videos on the Nostr network.',
  });

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCollectionForm, setShowCollectionForm] = useState(false);

  const { user } = useCurrentUser();
  const { data: collections } = useCollections(user?.pubkey);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Neon border accent */}
      <div className="fixed top-0 left-0 w-1 h-full bg-gradient-to-b from-pink-500 via-cyan-400 to-transparent opacity-50" />
      
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold">
              <span className="text-pink-500 drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]">
                NostrVidster
              </span>
            </h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <LoginArea />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* My Collections Section */}
        {user && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">My Collections</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCollectionForm(true)}
                className="text-pink-500 hover:text-pink-600"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                New Collection
              </Button>
            </div>

            {collections && collections.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {collections.map((collection) => {
                  const dTag = collection.tags.find(t => t[0] === 'd')?.[1] ?? collection.id;
                  const title = collection.tags.find(t => t[0] === 'title')?.[1] ?? 'Untitled';
                  const videoCount = collection.tags.filter(t => t[0] === 'e').length;
                  const npub = nip19.npubEncode(user.pubkey);

                  return (
                    <Link key={dTag} to={`/collection/${npub}/${dTag}`}>
                      <Card className="hover:bg-accent/50 transition-colors cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium">{title}</h3>
                              <p className="text-sm text-muted-foreground">
                                {videoCount} video{videoCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground mb-3">
                    No collections yet. Create one to organize your videos.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setShowCollectionForm(true)}
                    className="border-pink-500/50 text-pink-500 hover:bg-pink-500/10"
                  >
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Create Your First Collection
                  </Button>
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {/* Video Feed */}
        <section>
          {/* Floating action button for adding bookmarks */}
          <div className="fixed bottom-8 right-8 z-40">
            <Button
              size="lg"
              className="rounded-full shadow-lg shadow-pink-500/20 bg-pink-500 hover:bg-pink-600 text-white"
              onClick={() => setShowAddDialog(true)}
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Add Bookmark
            </Button>
          </div>

          <VideoFeed />
        </section>
      </main>

      <AddBookmarkDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
      <CollectionForm open={showCollectionForm} onOpenChange={setShowCollectionForm} />
    </div>
  );
};

export default Index;